import React, { useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { Divider, IconButton, ICON_BUTTON_SIZE_LG, ICON_BUTTON_VARIANT_TONAL } from '@aibuild/ui';
import { CREATE_APP_EXAMPLES, exampleTileImage, type CreateAppExample } from './createAppExamples.constants';
import { useRailScroll } from '@hooks/useRailScroll';
import {
  ArrowSlot,
  ExamplesRoot,
  ExamplesTitle,
  Rail,
  Strip,
  Tile,
  TileBrief,
  TileImage,
  TileName,
} from './CreateAppExamples.styled';

interface ExampleTileProps {
  readonly example: CreateAppExample;
  readonly onPick: (example: CreateAppExample) => void;
  readonly disabled: boolean;
}

const ExampleTile: React.FC<ExampleTileProps> = ({ example, onPick, disabled }) => {
  const intl = useIntl();
  const theme = useTheme();
  const [broken, setBroken] = useState(false);
  const name = intl.formatMessage(example.name);

  return (
    <Tile
      type="button"
      disabled={disabled}
      onClick={() => onPick(example)}
      aria-label={intl.formatMessage(
        { id: 'apps.builder.create.examples.use', defaultMessage: 'Build the {name} example' },
        { name },
      )}
      data-testid={`create-app__example-${example.id}`}
    >
      {broken ? (
        <TileBrief aria-hidden="true" data-testid={`create-app__example-${example.id}-text`}>
          {intl.formatMessage(example.brief)}
        </TileBrief>
      ) : (
        <TileImage
          src={exampleTileImage(example.id, theme.isLightTheme === true)}
          alt=""
          draggable={false}
          onError={() => setBroken(true)}
        />
      )}
      <TileName aria-hidden="true">{name}</TileName>
    </Tile>
  );
};

interface CreateAppExamplesProps {
  readonly onPick: (example: CreateAppExample) => void;
  readonly disabled?: boolean;
}

const CreateAppExamples: React.FC<CreateAppExamplesProps> = ({ onPick, disabled = false }) => {
  const intl = useIntl();
  const titleId = useId();
  const { railRef, measure, canPrevious, canNext, previous, next, previousButtonRef, nextButtonRef } =
    useRailScroll();

  return (
    <ExamplesRoot aria-labelledby={titleId} data-testid="create-app__examples">
      <Divider inset={0} />
      <ExamplesTitle id={titleId}>
        {intl.formatMessage({ id: 'apps.builder.create.examples.title', defaultMessage: 'Try one of these' })}
      </ExamplesTitle>
      <Rail>
        {canPrevious && (
          <ArrowSlot $side="start">
            <IconButton
              ref={previousButtonRef}
              ariaLabel={intl.formatMessage({
                id: 'apps.builder.create.examples.previous',
                defaultMessage: 'Previous examples',
              })}
              icon={<MdChevronLeft size={22} />}
              size={ICON_BUTTON_SIZE_LG}
              variant={ICON_BUTTON_VARIANT_TONAL}
              onClick={previous}
              dataTestId="create-app__examples-prev"
            />
          </ArrowSlot>
        )}
        <Strip ref={railRef} onScroll={measure}>
          {CREATE_APP_EXAMPLES.map((example) => (
            <ExampleTile key={example.id} example={example} onPick={onPick} disabled={disabled} />
          ))}
        </Strip>
        {canNext && (
          <ArrowSlot $side="end">
            <IconButton
              ref={nextButtonRef}
              ariaLabel={intl.formatMessage({
                id: 'apps.builder.create.examples.next',
                defaultMessage: 'Next examples',
              })}
              icon={<MdChevronRight size={22} />}
              size={ICON_BUTTON_SIZE_LG}
              variant={ICON_BUTTON_VARIANT_TONAL}
              onClick={next}
              dataTestId="create-app__examples-next"
            />
          </ArrowSlot>
        )}
      </Rail>
    </ExamplesRoot>
  );
};

export default CreateAppExamples;
